import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {BlankEntryCreationParams, EntryFromTemplateCreationParams} from 'dao/EntryDAO';
import {
  ProcedureParameterConfirmationMethod,
  StudyPhase,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {NotificationSchedule} from 'notifications/notification-schedule/NotificationScheduleExpressionUtils';
import {ExperimentConditionREST} from 'procedures/network/ProcedureRunDAO';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {patchJSONPromise, postJSON, postJSONPromise} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {ExecutionPlan} from './progress/Types';

export enum DesignEntryType {
  BLANK = 'blank',
  FROM_TEMPLATE = 'fromTemplate',
}
interface DesignEntryWithoutTemplateParams extends BlankEntryCreationParams {
  type: DesignEntryType.BLANK;
}

interface DesignEntryWithTemplateParams extends EntryFromTemplateCreationParams {
  type: DesignEntryType.FROM_TEMPLATE;
}
export type DesignEntryInformation = DesignEntryWithoutTemplateParams | DesignEntryWithTemplateParams;

// Keep in-sync with benchling/benchling_domain_graph/sdl/schemas/procedures/procedure_run.graphql:ProcedureRunDataEntryMode
export enum ProcedureRunDataEntryMode {
  INDIVIDUAL_CONDITION_REPLICATES = 'INDIVIDUAL_CONDITION_REPLICATES',
  ALL_CONDITION_REPLICATES_TOGETHER = 'ALL_CONDITION_REPLICATES_TOGETHER',
}

// Backend requires exactly one of templateProcedureFlowchartVersionId or workflowFlowchartConfigVersionId (XOR)
export type ProcedureInformation = (
  | {
      templateProcedureFlowchartVersionId: string;
      workflowFlowchartConfigVersionId?: never;
    }
  | {
      templateProcedureFlowchartVersionId?: never;
      workflowFlowchartConfigVersionId: string;
    }
) & {
  experimentConditions: Array<ExperimentConditionREST>;
  confirmationMethodByParamVersionId: Record<string, ProcedureParameterConfirmationMethod>;
  description: string;
  dataEntryMode?: ProcedureRunDataEntryMode;
};

export interface CreateStudyParams {
  name?: string; // name is undefined when naming template is set on study schema
  description?: string;
  folderId: string;
  studySchemaId: string;
  tags: TagUpdateObject;
  entryInformation?: DesignEntryInformation | null;
  // NOTE: This is a misnomer, as this key is used for both process development and assay development
  processDevInformation?: ProcedureInformation | null;
  displayId?: string;
}

export interface CreateStudiesResponse {
  createdIds: Array<string>;
}

export interface CreateExperimentsResponse {
  creates: Array<CreateExperimentResponse>;
}

export interface CreateExperimentResponse {
  id: string;
  folderId: string;
}

export interface UpdateStudyParams {
  name: string;
  description: string;
  folderId: string;
  tags: TagUpdateObject;
  phase: StudyPhase;
  displayId: string;
}

export interface UpdateStudyResponse {
  studyId: string;
}

export interface TaskResponse extends TaskRunner.OngoingTaskResponse {
  taskId: string;
  timingMeta: ExecutionPlan;
}

export async function createStudies(
  creates: BulkCreateStudiesParam['creates'],
  {supportsAsync}: {supportsAsync: boolean}
): Promise<
  | {async: false; data: CreateStudiesResponse}
  | {async: true; data: TaskResponse; taskMeta: TaskRunner.TaskMeta}
> {
  const url = `${getApiBaseForObjectType(DomainGraphType.STUDY)}?supportsAsync=${supportsAsync ? 1 : 0}`;
  // We use legacy postJSON because net.postJSONPromise / TaskRunner.runTask are too limited: we need to
  // inspect the response status code / headers since our response is dynamic and may either be a
  // synchronous web request or an asynchronous celery task, where our UX will reflect the asynchrony.
  const xhr: JQueryXHR = postJSON({
    url,
    data: {creates},
  }) as JQueryXHR;
  const response = await Promise.resolve(xhr);
  if (xhr.status === 202) {
    return {
      async: true,
      data: response as TaskResponse,
      taskMeta: TaskRunner.getTaskMeta(xhr, url),
    };
  } else {
    return {
      async: false,
      data: response as CreateStudiesResponse,
    };
  }
}

export async function createExperiments(
  studyId: string,
  creates: BulkCreateExperimentsParam['creates'],
  {supportsAsync}: {supportsAsync: boolean}
): Promise<
  | {async: false; data: CreateExperimentsResponse}
  | {async: true; data: TaskResponse; taskMeta: TaskRunner.TaskMeta}
> {
  const url = `${getApiBaseForObjectType(DomainGraphType.STUDY)}/${studyId}:experiments?supportsAsync=${
    supportsAsync ? 1 : 0
  }`;
  // We use legacy postJSON because net.postJSONPromise / TaskRunner.runTask are too limited: we need to
  // inspect the response status code / headers since our response is dynamic and may either be a
  // synchronous web request or an asynchronous celery task, where our UX will reflect the asynchrony.
  const xhr: JQueryXHR = postJSON({
    url,
    data: {creates},
  }) as JQueryXHR;
  const response = await Promise.resolve(xhr);
  if (xhr.status === 202) {
    return {
      async: true,
      data: response as TaskResponse,
      taskMeta: TaskRunner.getTaskMeta(xhr, url),
    };
  } else {
    return {
      async: false,
      data: response as CreateExperimentsResponse,
    };
  }
}

export interface BulkCreateStudiesParam {
  creates: Array<CreateStudyParams>;
}

export interface BulkCreateExperimentsParam {
  creates: Array<ProcedureInformation>;
}

export interface BulkUpdateStudiesParam {
  updates: Array<{id: string} & Partial<UpdateStudyParams>>;
}

export async function updateStudy(
  studyId: string,
  updateStudyParams: Partial<UpdateStudyParams>,
  dataModsHeader?: DataModificationsHeader
): Promise<UpdateStudyResponse> {
  const response = await patchJSONPromise({
    url: getApiBaseForObjectType(DomainGraphType.STUDY),
    data: {updates: [{id: studyId, ...updateStudyParams}]},
    extraHeaders: dataModsHeader,
  });
  assert(response.updatedIds.length === 1, 'Expected one study to be updated');
  assert(response.updatedIds[0] === studyId, 'Expected updated study to have the same id as the input study');
  return {studyId};
}

export function bulkUpdateStudies(data: BulkUpdateStudiesParam): Promise<BulkUpdateStudiesParam> {
  return patchJSONPromise({url: getApiBaseForObjectType(DomainGraphType.STUDY), data});
}

interface CreateReportResponse {
  newEntryId: string;
}

export function generateLLMReportFromEntries(
  reportTemplateId: string,
  entryIds: Array<string>,
  folderId: string,
  newEntryName: string,
  initialDay: string
): Promise<CreateReportResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/studies/report-generation:generate-report-from-entries',
    data: {reportTemplateId, entryIds, folderId, newEntryName, initialDay},
  });
}

export function generateLLMReportFromStudy(
  reportTemplateId: string,
  studyId: string,
  folderId: string,
  newEntryName: string,
  initialDay: string
): Promise<CreateReportResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/studies/report-generation:generate-report-from-study',
    data: {reportTemplateId, studyId, folderId, newEntryName, initialDay},
  });
}

interface NotificationScheduleUpdateParams {
  schedules: Array<NotificationSchedule>;
}
interface UpdateNotificationSchedulesResponse {}

export function updateNotificationSchedules(
  studyId: string,
  data: NotificationScheduleUpdateParams
): Promise<UpdateNotificationSchedulesResponse> {
  return postJSONPromise({
    url: `${getApiBaseForObjectType(DomainGraphType.STUDY)}/${studyId}:update_notification_schedules`,
    data,
  });
}

interface ExportStudyDesignResponse {
  url: string;
}

export function exportStudyDesign(studyId: string): Promise<ExportStudyDesignResponse> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/studies/${studyId}:export-design`,
  });
}
